import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, XCircle, CheckCircle2, Clock, Circle, Flag,
  User, Calendar, Trash2, Edit2, Send,
} from "lucide-react";
import { SkeletonList } from "@/components/admin/Skeleton";

const STATUS_META = {
  todo:        { label: "To Do",       color: "text-zinc-400 bg-zinc-600/10 border-zinc-600/20",       Icon: Circle },
  in_progress: { label: "In Progress", color: "text-blue-400 bg-blue-600/10 border-blue-600/20",       Icon: Clock },
  done:        { label: "Done",        color: "text-emerald-400 bg-emerald-600/10 border-emerald-600/20", Icon: CheckCircle2 },
};
const STATUS_ORDER = ["todo", "in_progress", "done"];

const PRIORITY_META = {
  low:    { label: "Low",    color: "text-zinc-400 border-zinc-600/30" },
  medium: { label: "Med",    color: "text-yellow-400 border-yellow-600/30" },
  high:   { label: "High",   color: "text-red-400 border-red-600/30" },
};

export default function TasksPanel({ API, getAuthHeaders, currentUser }) {
  const isAdmin = currentUser?.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | status | mine
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/tasks`, { headers: getAuthHeaders() });
      setTasks(data.tasks || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load tasks");
    }
    setLoading(false);
  }, [API, getAuthHeaders]);

  const fetchTeam = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/team`, { headers: getAuthHeaders() });
      setTeam(data || []);
    } catch {
      // non-fatal
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchTasks(); if (isAdmin) fetchTeam(); }, [fetchTasks, fetchTeam, isAdmin]);

  const updateTask = async (taskId, updates) => {
    try {
      const { data } = await axios.patch(`${API}/admin/tasks/${taskId}`, updates, { headers: getAuthHeaders() });
      setTasks(prev => prev.map(t => t.id === taskId ? data : t));
      toast.success("Task updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task permanently?")) return;
    try {
      await axios.delete(`${API}/admin/tasks/${taskId}`, { headers: getAuthHeaders() });
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success("Task deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  let filtered = tasks;
  if (filter === "mine") {
    filtered = tasks.filter(t => t.assigned_to === currentUser?.id || t.assigned_to === currentUser?._id);
  } else if (STATUS_ORDER.includes(filter)) {
    filtered = tasks.filter(t => t.status === filter);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-black uppercase tracking-tight">Tasks</h2>
          <p className="text-xs text-zinc-500">
            {isAdmin
              ? "Assign custom tasks to team members and track progress."
              : "Your assigned tasks — update status as you work."}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowNew(true)}
            data-testid="new-task-btn"
            className="btn-brutal btn-shine inline-flex items-center justify-center gap-2 bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-6 border-2 border-white hover:bg-white hover:text-black text-xs"
          >
            <Plus size={14} /> New Task
          </button>
        )}
      </div>

      {/* Filter chips */}
      <div className="flex flex-nowrap overflow-x-auto no-scrollbar gap-2">
        <FilterChip active={filter === "all"}    onClick={() => setFilter("all")}    label={`All (${tasks.length})`} testid="tasks-filter-all" />
        <FilterChip active={filter === "mine"}   onClick={() => setFilter("mine")}   label={`Mine (${tasks.filter(t => (t.assigned_to === currentUser?.id || t.assigned_to === currentUser?._id)).length})`} testid="tasks-filter-mine" />
        {STATUS_ORDER.map(s => (
          <FilterChip
            key={s}
            active={filter === s}
            onClick={() => setFilter(s)}
            label={`${STATUS_META[s].label} (${counts[s] || 0})`}
            color={STATUS_META[s].color}
            Icon={STATUS_META[s].Icon}
            testid={`tasks-filter-${s}`}
          />
        ))}
      </div>

      {loading ? <SkeletonList count={3} /> :
        filtered.length === 0 ? (
          <div className="border border-dashed border-white/10 bg-[#111] p-12 text-center">
            <CheckCircle2 size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-white font-bold uppercase tracking-wider text-sm">No tasks {filter !== "all" && filter !== "mine" ? `in "${STATUS_META[filter]?.label || filter}"` : filter === "mine" ? "assigned to you" : "yet"}</p>
            {isAdmin && filter === "all" && <p className="text-xs text-zinc-500 mt-1">Click "New Task" to assign one.</p>}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(t => (
              <TaskRow
                key={t.id}
                task={t}
                isAdmin={isAdmin}
                currentUserId={currentUser?.id || currentUser?._id}
                onStatusChange={(status) => updateTask(t.id, { status })}
                onEdit={() => setEditing(t)}
                onDelete={() => deleteTask(t.id)}
              />
            ))}
          </div>
        )
      }

      {showNew && (
        <TaskModal
          team={team}
          onClose={() => setShowNew(false)}
          onSave={async (payload) => {
            try {
              const { data } = await axios.post(`${API}/admin/tasks`, payload, { headers: getAuthHeaders() });
              setTasks(prev => [data, ...prev]);
              setShowNew(false);
              toast.success(`Task assigned to ${data.assigned_to_name}`);
              fetchTasks();
            } catch (err) {
              toast.error(err.response?.data?.detail || "Failed to create task");
            }
          }}
        />
      )}

      {editing && (
        <TaskModal
          team={team}
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            await updateTask(editing.id, payload);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// =============== Filter chip ===============
function FilterChip({ active, onClick, label, color, Icon, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`tap shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest border px-3 py-2 transition-colors ${active ? (color || "bg-red-600 border-red-600 text-white") + " border-current" : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}
    >
      {Icon && <Icon size={11} />} {label}
    </button>
  );
}

// =============== Task Row ===============
function TaskRow({ task, isAdmin, currentUserId, onStatusChange, onEdit, onDelete }) {
  const meta = STATUS_META[task.status] || STATUS_META.todo;
  const pr = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const MetaIcon = meta.Icon;
  const overdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== "done";
  const isMine = task.assigned_to === currentUserId;

  return (
    <div data-testid={`task-row-${task.id}`} className="border border-white/5 bg-[#111] p-4 sm:p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border px-2 py-0.5 ${meta.color}`}>
            <MetaIcon size={10} /> {meta.label}
          </span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border px-2 py-0.5 ${pr.color}`}>
            <Flag size={10} /> {pr.label}
          </span>
          {overdue && <span className="text-[10px] font-bold uppercase tracking-widest border border-red-600/60 text-red-400 bg-red-600/10 px-2 py-0.5">Overdue</span>}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1">
            <button onClick={onEdit} className="tap touch-target inline-flex items-center justify-center text-zinc-400 hover:text-white" title="Edit">
              <Edit2 size={14} />
            </button>
            <button onClick={onDelete} className="tap touch-target inline-flex items-center justify-center text-zinc-400 hover:text-red-500" title="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-base font-black text-white">{task.title}</h3>
        {task.description && <p className="text-xs text-zinc-400 mt-1 whitespace-pre-wrap">{task.description}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-zinc-500">
        <span className="flex items-center gap-1"><User size={11} /> {task.assigned_to_name}{isMine && <span className="text-red-500 font-bold ml-1">(you)</span>}</span>
        {task.due_date && <span className={`flex items-center gap-1 ${overdue ? "text-red-400" : ""}`}><Calendar size={11} /> {new Date(task.due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>}
        <span className="text-zinc-600">by {task.created_by_name}</span>
      </div>

      {/* Status quick-change (admin OR assignee) */}
      {(isAdmin || isMine) && (
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-white/5">
          {STATUS_ORDER.map(s => {
            const m = STATUS_META[s];
            const SIcon = m.Icon;
            const active = task.status === s;
            return (
              <button
                key={s}
                onClick={() => !active && onStatusChange(s)}
                data-testid={`task-${task.id}-status-${s}`}
                disabled={active}
                className={`tap inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border px-2.5 py-1 transition-colors ${active ? `${m.color} border-current cursor-default` : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}
              >
                <SIcon size={10} /> {m.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============== Create / Edit modal ===============
function TaskModal({ team, initial, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    assigned_to: initial?.assigned_to || (team[0]?.id || team[0]?._id || ""),
    priority: initial?.priority || "medium",
    due_date: initial?.due_date ? initial.due_date.slice(0, 10) : "",
  });

  useEffect(() => {
    document.body.classList.add("no-scroll");
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.assigned_to) return toast.error("Please pick an assignee");
    setLoading(true);
    await onSave({
      ...form,
      due_date: form.due_date || null,
    });
    setLoading(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 animate-fadeIn"
      data-testid="task-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-xl h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto relative">
        <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-black uppercase tracking-tight">{initial ? "Edit Task" : "New Task"}</h2>
          <button onClick={onClose} className="tap touch-target inline-flex items-center justify-center text-zinc-400 hover:text-white" aria-label="Close">
            <XCircle size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Title *</label>
            <input
              required
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Design new landing page mockup"
              data-testid="task-title"
              autoFocus
              className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Description / Instructions</label>
            <textarea
              rows={4}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Share brief, context, links, expected deliverables..."
              data-testid="task-description"
              className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none resize-none"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Assign to *</label>
              <select
                required
                value={form.assigned_to}
                onChange={e => setForm({ ...form, assigned_to: e.target.value })}
                data-testid="task-assignee"
                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none"
              >
                <option value="">Select...</option>
                {team.map(m => (
                  <option key={m.id || m._id} value={m.id || m._id}>
                    {(m.display_name || m.name || m.email)} {m.role === "admin" ? "(Admin)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Priority</label>
              <select
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}
                data-testid="task-priority"
                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Due Date (optional)</label>
            <input
              type="date"
              value={form.due_date}
              onChange={e => setForm({ ...form, due_date: e.target.value })}
              data-testid="task-due-date"
              className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="task-submit"
            className="btn-brutal btn-shine w-full bg-red-600 text-white font-bold uppercase tracking-widest py-4 px-6 border-2 border-white hover:bg-white hover:text-black text-sm disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
          >
            {loading ? <><Loader2 size={14} className="animate-spin" /> Saving...</>
              : <><Send size={14} /> {initial ? "Save Changes" : "Assign Task"}</>}
          </button>
        </form>
      </div>
    </div>
  );
}
